import React, { useState, useEffect } from "react";
import {
  Users,
  Award,
  Target,
  Zap,
  X,
  Mail,
  Linkedin,
  Twitter,
} from "lucide-react";

const TeamPage = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const response = await fetch("https://archpoint.onrender.com/api/team");
      if (!response.ok) throw new Error("Failed to fetch team data");
      const data = await response.json();
      setTeamMembers(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-yellow-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-yellow-500 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-xl font-bold text-gray-800">
            Loading our amazing team...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-3xl shadow-2xl max-w-md border-4 border-yellow-400">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Oops! Something went wrong
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchTeamData}
            className="bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 text-gray-900 px-6 py-2 rounded-xl font-bold hover:shadow-lg transform hover:scale-105 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen mt-[70px]
 bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50"
    >
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 py-16 px-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -left-1/4 w-96 h-96 bg-yellow-300 rounded-full opacity-30 blur-3xl animate-pulse"></div>
          <div
            className="absolute -bottom-1/2 -right-1/4 w-96 h-96 bg-orange-300 rounded-full opacity-30 blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="relative max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4 shadow-xl">
            <Users className="w-8 h-8 text-yellow-600" />
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-4">
            Meet Our <span className="text-white">Dream Team</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-800 max-w-2xl mx-auto">
            Innovation meets passion. Together, we're building something
            extraordinary.
          </p>
        </div>
      </div>

      {/* Team Grid - Compact Cards with Circular Images */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {teamMembers.map((member, index) => (
            <div
              key={member._id || index}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer transform hover:-translate-y-2"
              onClick={() => setSelectedMember(member)}
              style={{
                animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
              }}
            >
              {/* Circular Image Container */}
              <div className="relative pt-8 pb-4">
                <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-yellow-400 shadow-lg relative group-hover:border-amber-500 transition-all">
                  <img
                    src={member?.image?.url || "/api/placeholder/300/300"}
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    style={{ objectPosition: "center" }}
                    onError={(e) => {
                      e.target.src = "/api/placeholder/300/300";
                    }}
                  />
                </div>

                {/* Status Badge */}
                <div className="absolute top-4 right-4 w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow-sm"></div>
              </div>

              {/* Content */}
              <div className="px-6 pb-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  {member.name}
                </h3>

                <div className="inline-block bg-gradient-to-r from-yellow-400 to-amber-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
                  {member.role}
                </div>

                <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4">
                  {member.bio ||
                    "Passionate team member dedicated to excellence and innovation"}
                </p>

                <button className="text-yellow-600 font-semibold text-sm hover:text-amber-600 transition-colors flex items-center justify-center gap-1 mx-auto group">
                  <span>View Profile</span>
                  <span className="group-hover:translate-x-1 transition-transform">
                    →
                  </span>
                </button>
              </div>

              {/* Hover Accent */}
              <div className="h-1 bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
            </div>
          ))}
        </div>

        {teamMembers.length === 0 && !loading && !error && (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
              <Users className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              No Team Members Yet
            </h3>
            <p className="text-lg text-gray-600">
              Check back soon to meet our amazing team!
            </p>
          </div>
        )}
      </div>

      {/* Stats Section */}
      {teamMembers.length > 0 && (
        <div className="relative bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 py-12 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-5"></div>
          <div className="relative max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: Award, value: "100%", label: "Dedicated" },
                { icon: Target, value: "24/7", label: "Support" },
                { icon: Zap, value: "∞", label: "Innovation" },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="text-center bg-white/20 backdrop-blur-sm rounded-xl p-5 hover:bg-white/30 transition-all transform hover:scale-105"
                >
                  <stat.icon className="w-10 h-10 text-gray-900 mx-auto mb-2" />
                  <div className="text-3xl font-extrabold text-gray-900 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-gray-800 font-semibold">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal for Full Profile */}
      {selectedMember && (
        <div
          className="fixed inset-0 pt-[150px]
 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMember(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideUp 0.4s ease-out" }}
          >


            {/* Modal Header with Circular Image */}
            <div className="relative bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 p-8 text-center">
              <div className="w-40 h-40 mx-auto rounded-full overflow-hidden border-6 border-white shadow-2xl mb-4">
                <img
                  src={selectedMember?.image?.url || "/api/placeholder/400/400"}
                  alt={selectedMember.name}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center" }}
                />
              </div>
              <button
                onClick={() => setSelectedMember(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white hover:bg-gradient-to-r hover:from-yellow-400 hover:to-amber-400 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-lg z-10"
              >
                <X className="w-5 h-5 text-gray-900" />
              </button>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">
                {selectedMember.name}
              </h2>
              <div className="inline-block bg-white text-yellow-600 px-6 py-2 rounded-full font-bold text-sm uppercase tracking-wider shadow-lg">
                {selectedMember.role}
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <div className="w-1 h-6 bg-gradient-to-b from-yellow-400 to-amber-400 rounded-full"></div>
                About
              </h3>

              <p className="text-gray-700 text-base leading-relaxed mb-6">
                {selectedMember.bio ||
                  "Passionate team member dedicated to excellence and innovation. Committed to delivering exceptional results and contributing to our team's success."}
              </p>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default TeamPage;
